<template>
  <component
    :is="layoutComponent"
    page-title="Well Head Sheet"
    :page-subtitle="form.well_name || 'Digital field sheet'"
  >
    <div class="well-sheet-page">
      <div class="page-toolbar no-print">
        <div>
          <a href="#" class="back-link" @click.prevent="goBack">← Back to Well Head Sheets</a>
          <h1>{{ form.title || 'Well Head Sheet' }}</h1>
          <p>Digital field sheet modeled after the paper well-head report.</p>
        </div>

        <div class="toolbar-actions">
          <span v-if="saveState" class="save-state">{{ saveState }}</span>
          <button type="button" class="secondary-btn" @click="printSheet">Print</button>
          <button type="button" class="danger-btn" :disabled="deleting" @click="deleteSheet">
            {{ deleting ? 'Deleting…' : 'Delete' }}
          </button>
          <button type="button" class="primary-btn" :disabled="saving" @click="saveSheet">
            {{ saving ? 'Saving…' : 'Save' }}
          </button>
        </div>
      </div>

      <p v-if="error" class="error-line no-print">{{ error }}</p>
      <p v-if="loading" class="loading-line no-print">Loading sheet…</p>

      <form v-else class="paper-sheet" @submit.prevent="saveSheet">
        <header class="sheet-header">
          <section class="header-fields left-fields">
            <label class="lined-field"><span class="field-label">Customer</span><input v-model="form.customer" /></label>
            <label class="lined-field"><span class="field-label">Well Name</span><input v-model="form.well_name" /></label>
            <label class="lined-field"><span class="field-label">L.S.D.</span><input v-model="form.lsd" /></label>
            <label class="lined-field"><span class="field-label">Co. Rep.</span><input v-model="form.co_rep" /></label>
            <label class="lined-field"><span class="field-label">Well Site Sup.</span><input v-model="form.well_site_sup" /></label>
          </section>

          <CompanyLogoUpload v-if="!isEmployee" :existing="form.logo_path" @file="uploadLogo" />
          <img v-else-if="form.logo_path" :src="form.logo_path" alt="Company logo" class="company-logo" />

          <section class="header-fields right-fields">
            <label class="lined-field"><span class="field-label">Test Sup.</span><input v-model="form.test_sup" /></label>
            <label class="lined-field"><span class="field-label">Meter type &amp; size</span><input v-model="form.meter_type_size" /></label>

            <div class="flowing-field">
              <span class="field-label">Flowing</span>
              <div class="flowing-values">
                <label>Tbg.<input v-model="form.flowing_tbg" /></label>
                <label>Csg.<input v-model="form.flowing_csg" /></label>
                <label>Size<input v-model="form.flowing_size" /></label>
              </div>
            </div>

            <label class="lined-field"><span class="field-label">Zone and Interval</span><input v-model="form.zone_interval" /></label>
          </section>
        </header>

        <div class="table-scroll">
          <table class="well-data-table">
            <thead>
              <tr class="group-row">
                <th colspan="5">Well Head Data</th>
                <th colspan="5">Gas Meter Data</th>
                <th colspan="8">Fluid Data</th>
              </tr>
              <tr class="column-row">
                <th>Time</th>
                <th>Flow<br><span>°F</span></th>
                <th>Tubing<br><span>Psi</span></th>
                <th>Csg.<br><span>Psi</span></th>
                <th>Choke<br><span>64's</span></th>
                <th>Orifice<br><span>in</span></th>
                <th>Diff</th>
                <th>Static<br><span>psi</span></th>
                <th>M.R.<br><span>Temp</span></th>
                <th>Gas<br><span>Rate</span></th>
                <th>Incremental<br><span>Fluid</span></th>
                <th>Inc.<br><span>Oil</span></th>
                <th>Inc.<br><span>Water</span></th>
                <th>Rate bpm</th>
                <th>B.S.W.</th>
                <th>Salinity</th>
                <th>WW</th>
                <th>Sand</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(row, r) in form.rows" :key="row.time">
                <td class="time-cell">{{ row.time }}</td>
                <td v-for="(field, c) in rowFields" :key="field">
                  <input
                    :ref="(el) => setCellRef(r, c, el)"
                    v-model="row[field]"
                    inputmode="decimal"
                    @keydown="onKey($event, r, c)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <section class="sheet-footer">
          <div class="remarks-box">
            <label>Remarks:</label>
            <textarea v-model="form.remarks" rows="7"></textarea>
            <div class="fluid-shipped-row">
              <strong>Total Fluid Shipped off Well Site</strong>
              <input v-model="form.total_fluid_shipped" inputmode="decimal" />
            </div>
          </div>

          <div class="cumulative-box">
            <label v-for="field in cummFields" :key="field.key" class="cumm-field">
              <span>{{ field.label }}</span>
              <span>(</span>
              <input v-model="form[field.key]" inputmode="decimal" />
              <span>hrs.)</span>
              <i></i>
            </label>
          </div>
        </section>
      </form>
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'
import AppLayout from '@/components/layout/AppLayout.vue'
import CompanyLogoUpload from '@/components/wellhead/CompanyLogoUpload.vue'

const route = useRoute()
const router = useRouter()
const sheetId = String(route.params.id)
const isEmployee = computed(() => !route.path.startsWith('/company'))
const fieldHome = computed(() => route.path.startsWith('/employee') ? '/employee' : '')
const layoutComponent = computed(() => isEmployee.value ? AppLayout : CompanyAppLayout)
const apiBase = computed(() => `/${isEmployee.value ? 'api/employee' : 'api/company'}/well-head-sheets`)

const rowFields = [
  'flow_f', 'tubing_psi', 'csg_psi', 'choke_64', 'orifice_in', 'diff', 'static_psi',
  'mr_temp', 'gas_rate', 'incremental_fluid', 'inc_oil', 'inc_water', 'rate_bpm',
  'bsw', 'salinity', 'ww', 'sand',
] as const

const cummFields = [
  { key: 'gas_cumm_past', label: 'Gas cumm past' },
  { key: 'fluid_cumm_past', label: 'Fluid cumm past' },
  { key: 'oil_cumm_past', label: 'Oil cumm past' },
  { key: 'water_cumm_past', label: 'Water cumm past' },
  { key: 'total_gas_cumm', label: 'Total gas cumm' },
  { key: 'total_fluid_cumm', label: 'Total fluid cumm' },
  { key: 'total_oil_cumm', label: 'Total oil cumm' },
  { key: 'total_water_cumm', label: 'Total water cumm' },
] as const

const loading = ref(false)
const saving = ref(false)
const deleting = ref(false)
const error = ref('')
const saveState = ref('')
const cellRefs = new Map<string, HTMLInputElement>()

const form = reactive<Record<string, any>>({
  title: '',
  report_date: '',
  customer: '',
  well_name: '',
  lsd: '',
  co_rep: '',
  well_site_sup: '',
  test_sup: '',
  meter_type_size: '',
  flowing_tbg: '',
  flowing_csg: '',
  flowing_size: '',
  zone_interval: '',
  remarks: '',
  total_fluid_shipped: '',
  logo_path: '',
  rows: [] as Record<string, string>[],
})

function authHeaders(json = true) {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`,
  }
  if (json) headers['Content-Type'] = 'application/json'
  return headers
}

function setCellRef(r: number, c: number, el: any) {
  if (el) cellRefs.set(`${r}:${c}`, el as HTMLInputElement)
}

function focusCell(r: number, c: number) {
  if (r < 0 || c < 0 || r >= form.rows.length || c >= rowFields.length) return
  const el = cellRefs.get(`${r}:${c}`)
  el?.focus()
  el?.select()
}

function onKey(e: KeyboardEvent, r: number, c: number) {
  const input = e.target as HTMLInputElement
  switch (e.key) {
    case 'ArrowUp':
      e.preventDefault(); focusCell(r - 1, c); break
    case 'ArrowDown':
    case 'Enter':
      e.preventDefault(); focusCell(r + 1, c); break
    case 'ArrowLeft':
      if (input.selectionStart === 0) { e.preventDefault(); focusCell(r, c - 1) }
      break
    case 'ArrowRight':
      if (input.selectionStart === input.value.length) { e.preventDefault(); focusCell(r, c + 1) }
      break
    default:
      if (e.key === 's' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); saveSheet() }
  }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const res = await fetch(`${apiBase.value}/${sheetId}`, { headers: authHeaders() })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    const sheet = data.sheet || {}
    if (isEmployee.value) {
      sheet.report_date = sheet.report_date ?? sheet.reportDate
      sheet.well_name = sheet.well_name ?? sheet.wellName
      sheet.rows = (data.rows || []).map((row: any) => ({
        time: row.timeSlot,
        flow_f: row.flowTemp,
        tubing_psi: row.tubingPsi,
        csg_psi: row.casingPsi,
        choke_64: row.choke64s,
        orifice_in: row.orifice,
        diff: row.diff,
        static_psi: row.staticPsi,
        mr_temp: row.meterTemp,
        gas_rate: row.gasRate,
        incremental_fluid: row.incrementalFluid,
        inc_oil: row.incrementalOil,
        inc_water: row.incrementalWater,
        rate_bpm: row.rateBpm,
        bsw: row.bsw,
        salinity: row.salinity,
        sand: row.sand,
      }))
    }
    Object.keys(form).forEach((key) => {
      if (key === 'rows') return
      if (sheet[key] !== undefined && sheet[key] !== null) form[key] = sheet[key]
    })
    form.rows = (sheet.rows || []).map((r: Record<string, string>) => ({ ...r }))
  } catch (e: any) {
    error.value = e.message || 'Failed to load well head sheet'
  } finally {
    loading.value = false
  }
}

async function saveSheet() {
  saving.value = true
  error.value = ''
  try {
    const payload: Record<string, unknown> = { title: form.title, reportDate: form.report_date }
    for (const key of Object.keys(form)) {
      if (key === 'rows' || key === 'logo_path' || key === 'title' || key === 'report_date') continue
      payload[key] = form[key]
    }
    payload.rows = form.rows
    const res = await fetch(`${apiBase.value}/${sheetId}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    saveState.value = `Saved ${new Date().toLocaleTimeString()}`
  } catch (e: any) {
    error.value = e.message || 'Save failed'
  } finally {
    saving.value = false
  }
}

async function uploadLogo(file: File | null) {
  if (!file) return
  if (isEmployee.value) {
    error.value = 'Logo changes are managed by the company.'
    return
  }
  const data = new FormData()
  data.append('logo', file)
  try {
    const res = await fetch(`/api/company/well-head-sheets/${sheetId}/logo`, {
      method: 'POST',
      headers: authHeaders(false),
      body: data,
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const { logoPath } = await res.json()
    form.logo_path = logoPath
    saveState.value = 'Logo uploaded'
  } catch (e: any) {
    error.value = e.message || 'Logo upload failed'
  }
}

async function deleteSheet() {
  if (!window.confirm('Delete this well head sheet? This cannot be undone.')) return
  deleting.value = true
  error.value = ''
  try {
    const res = await fetch(`${apiBase.value}/${sheetId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
    goBack()
  } catch (e: any) {
    error.value = e.message || 'Delete failed'
  } finally {
    deleting.value = false
  }
}

function printSheet() {
  window.print()
}

function goBack() {
  router.push(isEmployee.value ? `${fieldHome.value}/well-head-sheets` || '/well-head-sheets' : '/company/well-head-sheets')
}

onMounted(load)
</script>

<style scoped>
.well-sheet-page {
  padding: 16px 0 32px;
  color: #dce7f6;
}

.page-toolbar {
  max-width: 1500px;
  margin: 0 auto 18px;
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 16px;
}

.page-toolbar h1 { margin: 8px 0 3px; font-size: 24px; color: #fff; }
.page-toolbar p { margin: 0; color: #7f94af; font-size: 13px; }
.back-link { color: #8fb9dc; text-decoration: none; font-size: 13px; }
.toolbar-actions { display: flex; gap: 10px; align-items: center; }
.save-state { color: #34d399; font-size: 12px; }
.error-line { color: #f87171; max-width: 1500px; margin: 0 auto 12px; }
.loading-line { color: #94a3b8; max-width: 1500px; margin: 0 auto 12px; }
.toolbar-actions button { border: 0; border-radius: 8px; padding: 10px 18px; font-weight: 700; cursor: pointer; }
.toolbar-actions button:disabled { opacity: .6; cursor: default; }
.secondary-btn { background: #26364d; color: #e9f1fb; }
.danger-btn { background: #411b23; color: #ffc4cc; border-color: #8f3c49; }
.primary-btn { background: #0f7d69; color: white; }

.paper-sheet {
  --ink: #233246;
  --line: #566579;
  max-width: 1500px;
  margin: 0 auto;
  background: #edf1f4;
  color: var(--ink);
  border: 1px solid #9ba7b4;
  box-shadow: 0 18px 50px rgba(0,0,0,.28);
  padding: 18px 22px 20px;
  font-family: Arial, Helvetica, sans-serif;
}

.sheet-header {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) 220px minmax(360px, 1.25fr);
  gap: 22px;
  align-items: start;
  margin-bottom: 10px;
}

.header-fields { display: grid; gap: 4px; }
.lined-field, .flowing-field { display: grid; grid-template-columns: 128px minmax(0, 1fr); align-items: center; min-height: 25px; }
.field-label { font-size: 15px; font-weight: 500; }
.lined-field input,
.flowing-field input {
  width: 100%;
  height: 23px;
  border: 0;
  border-bottom: 2px solid var(--line);
  outline: 0;
  background: transparent;
  color: var(--ink);
  padding: 1px 5px;
  font: inherit;
}

.flowing-values { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; }
.flowing-values label { display: grid; grid-template-columns: auto 1fr; align-items: center; gap: 5px; font-size: 15px; }

.table-scroll { overflow-x: auto; }
.well-data-table { width: 100%; min-width: 1250px; border-collapse: collapse; table-layout: fixed; font-size: 12px; }
.well-data-table th,
.well-data-table td { border: 1.35px solid var(--line); padding: 0; text-align: center; }
.group-row th { font-size: 15px; font-weight: 700; padding: 3px 0; }
.column-row th { height: 45px; line-height: 1.06; font-size: 12px; font-weight: 500; }
.column-row th span { font-size: 11px; }
.well-data-table tbody tr { height: 25px; }
.well-data-table td input {
  width: 100%;
  height: 24px;
  box-sizing: border-box;
  border: 0;
  outline: 0;
  background: transparent;
  text-align: center;
  color: var(--ink);
  padding: 0 2px;
  font-size: 12px;
}
.well-data-table td input:focus { background: rgba(15, 125, 105, .12); box-shadow: inset 0 0 0 2px #0f7d69; }
.time-cell { width: 55px; font-weight: 600; }

.sheet-footer { display: grid; grid-template-columns: 1.45fr 1fr; border: 1.35px solid var(--line); border-top: 0; }
.remarks-box { border-right: 1.35px solid var(--line); min-height: 210px; display: grid; grid-template-rows: auto 1fr auto; }
.remarks-box > label { font-weight: 700; padding: 3px 5px 0; }
.remarks-box textarea { border: 0; outline: 0; resize: vertical; min-height: 140px; background: repeating-linear-gradient(to bottom, transparent 0 24px, rgba(86,101,121,.45) 25px); padding: 5px; color: var(--ink); font: inherit; line-height: 25px; }
.fluid-shipped-row { border-top: 1.35px solid var(--line); display: grid; grid-template-columns: auto 1fr; min-height: 34px; align-items: center; }
.fluid-shipped-row strong { padding: 0 8px; font-size: 14px; }
.fluid-shipped-row input { height: 100%; border: 0; border-left: 1.35px solid var(--line); background: transparent; outline: 0; padding: 0 8px; color: var(--ink); }

.cumulative-box { display: grid; grid-auto-rows: 1fr; }
.cumm-field { display: grid; grid-template-columns: 130px auto 60px auto 1fr; align-items: center; gap: 3px; min-height: 26px; padding: 0 4px; font-size: 12px; }
.cumm-field input { width: 56px; border: 0; border-bottom: 1px solid var(--line); background: transparent; outline: 0; text-align: center; color: var(--ink); }
.cumm-field i { border-bottom: 1px solid var(--line); height: 12px; }

@media (max-width: 1050px) {
  .sheet-header { grid-template-columns: 1fr 180px; }
  .right-fields { grid-column: 1 / -1; }
  .sheet-footer { grid-template-columns: 1fr; }
  .remarks-box { border-right: 0; border-bottom: 1.35px solid var(--line); }
}

@media (max-width: 700px) {
  .page-toolbar { align-items: flex-start; flex-direction: column; }
  .sheet-header { grid-template-columns: 1fr; }
  .right-fields { grid-column: auto; }
  .logo-uploader { order: -1; }
  .lined-field, .flowing-field { grid-template-columns: 108px 1fr; }
}

@media print {
  .no-print { display: none !important; }
  .well-sheet-page { background: white; padding: 0; }
  .paper-sheet { box-shadow: none; border: 0; max-width: none; padding: 4mm; }
  .table-scroll { overflow: visible; }
  .well-data-table { min-width: 0; font-size: 8px; }
  .well-data-table td input { font-size: 8px; }
  .sheet-header { grid-template-columns: 1fr 38mm 1.25fr; gap: 5mm; }
}
</style>
